/**
 * How much of a booking is taken up front.
 *
 * The deposit used to be a bare percentage of the grand total, which on a cheap
 * trip produced a figure too small to be worth collecting — 25% of an $80
 * transfer is $20. Operators wanted a floor: "25%, but never less than $150".
 *
 * This module owns that rule for BOTH the server that charges (the
 * create-payment-intent route, the payment-mode route) and the pages that
 * display it (the booking flow's Order Summary). Sharing one implementation is
 * the point: the amount shown to the customer and the amount charged to their
 * card cannot drift apart, in the same way `applyQuoteDiscount` is shared by
 * checkout and the payment page.
 *
 * Everything here is pure and immutable.
 */

const roundCents = (n: number): number => Math.round(n * 100) / 100

/**
 * Default copy for the optional deposit notice at checkout. Shown only when the
 * account enables `booking_deposit_notice_enabled`; a custom
 * `booking_deposit_notice_text` replaces it. Shared so the Builder placeholder
 * and the viewer fallback can never drift apart.
 */
export const DEFAULT_DEPOSIT_NOTICE_TEXT = 'Please note: Deposits are non-refundable.'

/** A deposit rule as configured on the account. */
export interface DepositRule {
  /** Percentage of the grand total, 0-100. */
  percentage: number
  /** Never take less than this many dollars. 0 disables the floor. */
  minimumAmount: number
}

/** What a deposit rule resolved to for one trip. */
export interface ResolvedDeposit {
  /** Dollars to charge now. Never more than `total`. */
  amount: number
  /**
   * Which half of the rule produced `amount`.
   *
   * `percentage` — the percentage was the higher of the two.
   * `minimum`    — the floor was higher, or the floor met/exceeded the fare.
   */
  basis: 'percentage' | 'minimum'
  /**
   * The proportion of the total that `amount` represents, to 2dp.
   *
   * Stored on the booking as `deposit_percentage` and rendered as
   * "Deposit Paid (25%)". This is the EFFECTIVE percentage, not the configured
   * one: when the floor wins, a $150 deposit on a $400 fare is 37.5%, and
   * printing the configured 25% next to it would simply be a lie.
   */
  effectivePercentage: number
  /**
   * True when the deposit swallowed the whole fare — the floor met or exceeded
   * the total, so the customer is paying in full and no balance remains.
   *
   * Callers that write bookings should treat this as "not really a deposit":
   * it produces `balance_due = 0`, so the row never appears in the
   * balance-owed list or the reminder job.
   */
  coversFullTotal: boolean
}

/**
 * Resolve a deposit rule against a trip total.
 *
 *     deposit = min( total, max( minimumAmount, total × percentage / 100 ) )
 *
 * The `min(total, ...)` clamp is the case worth knowing about: a $150 floor on a
 * $120 fare charges $120, in full. A deposit larger than the thing being
 * deposited against is never charged.
 *
 * A non-finite or negative input is treated as absent rather than propagating
 * NaN into a Stripe amount: a broken setting must not be able to charge a
 * nonsense figure.
 *
 * For a round trip, pass the COMBINED total. The floor applies once per trip —
 * applying it per leg would charge a $150 minimum twice — and the resulting
 * amount is then split across the two booking rows by `allocateAmountByShare`.
 */
export function resolveDepositAmount(total: number, rule: DepositRule): ResolvedDeposit {
  const safeTotal = Number.isFinite(total) && total > 0 ? roundCents(total) : 0

  const safePercentage =
    Number.isFinite(rule.percentage) && rule.percentage > 0
      ? Math.min(rule.percentage, 100)
      : 0
  const safeMinimum =
    Number.isFinite(rule.minimumAmount) && rule.minimumAmount > 0 ? roundCents(rule.minimumAmount) : 0

  const fromPercentage = roundCents(safeTotal * (safePercentage / 100))
  const uncapped = Math.max(fromPercentage, safeMinimum)

  // The floor wins on ties too: if both halves produce the same figure, the
  // basis reported is `percentage`, since that is the rule the operator thinks
  // in and the one the effective percentage will agree with.
  const basis: ResolvedDeposit['basis'] = safeMinimum > fromPercentage ? 'minimum' : 'percentage'

  const amount = roundCents(Math.min(safeTotal, uncapped))
  const coversFullTotal = safeTotal > 0 && amount >= safeTotal

  return {
    amount,
    basis,
    effectivePercentage: safeTotal > 0 ? roundCents((amount / safeTotal) * 100) : 0,
    coversFullTotal,
  }
}

/**
 * Whether a deposit rule can actually take a deposit.
 *
 * Both halves at zero means "charge nothing now", which would book the trip for
 * free. The Builder rejects that combination at save time; this predicate is the
 * server's second line of defence for settings written before that validation
 * existed, or by any other path.
 */
export function isDepositRuleUsable(rule: DepositRule): boolean {
  const hasPercentage = Number.isFinite(rule.percentage) && rule.percentage > 0
  const hasMinimum = Number.isFinite(rule.minimumAmount) && rule.minimumAmount > 0
  return hasPercentage || hasMinimum
}
